import { useEffect, useState } from "react";
import { Archive, FileText, Lock } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { toast } from "sonner";
import { BRAND_COLORS, withAlpha } from "../lib/bookingStatus";
import { deDateMedium } from "../lib/dateFormat";
import { deCurrency } from "../lib/numberFormat";

/**
 * Feb 2026 · DSGVO §147 AO · Rechnungsarchiv (5 Sep 2026 · User-Directive).
 *
 * „Rechnungen muessen in Deutschland 10 Jahre aufbewahrt werden — auch
 * nach einer Konto-Loeschung. Nur Nutzer mit dem is_finance_admin-Schalter
 * koennen diese archivierten Rechnungen noch einsehen. Normale Admins
 * sehen nichts davon."
 *
 * Route ist von `App.js` fuer alle Admins offen, aber der Backend-Endpoint
 * (`GET /api/finance/invoice-archive`) wirft 403 fuer Nicht-Finance-Admins.
 * Wir zeigen dann ein klar formuliertes Verboten-Panel; die Sidebar-Nav
 * blendet den Link ohnehin nur fuer Finance-Admins ein.
 */
export default function TeamInvoiceArchive() {
  const [state, setState] = useState({ loading: true, rows: [], forbidden: false });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/finance/invoice-archive");
        setState({ loading: false, rows: data, forbidden: false });
      } catch (e) {
        if (e.response?.status === 403) {
          setState({ loading: false, rows: [], forbidden: true });
        } else {
          setState({ loading: false, rows: [], forbidden: false });
          toast.error(formatErr(e.response?.data?.detail) || e.message);
        }
      }
    })();
  }, []);

  const fmtEur = (n) => deCurrency(Number(n) || 0, "EUR");
  const fmtDate = (iso) => iso ? deDateMedium(iso) : "—";

  if (state.forbidden) {
    return (
      <div className="p-6 md:p-10 max-w-3xl mx-auto" data-testid="finance-archive-forbidden">
        <div className="bg-white border p-8" style={{ borderColor: withAlpha(BRAND_COLORS.navy, 0.15) }}>
          <div className="flex items-center gap-3 mb-4">
            <Lock className="w-5 h-5" strokeWidth={1.6} style={{ color: "#8B1E1E" }} />
            <div className="label-caps" style={{ color: "#8B1E1E" }}>Zugriff verweigert</div>
          </div>
          <h1 className="font-display text-2xl md:text-3xl text-navy mb-3">
            Kein Zugriff auf das Rechnungsarchiv
          </h1>
          <p className="text-[13px] text-navy-dark/80 leading-relaxed">
            Das Rechnungsarchiv nach §147 AO ist ausschliesslich fuer Nutzer
            mit dem <span className="font-semibold">Finance-Admin</span>-Flag
            zugaenglich. Bitten Sie den Owner-Admin, Sie unter „Zugaenge"
            entsprechend freizuschalten.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-10 max-w-[1200px] mx-auto" data-testid="finance-archive-page">
      <div className="mb-8">
        <div className="label-caps mb-2 flex items-center gap-2">
          <Archive className="w-3.5 h-3.5" strokeWidth={1.6} /> Rechnungsarchiv · §147 AO
        </div>
        <h1 className="font-display text-3xl md:text-4xl text-navy leading-tight">
          Archivierte Rechnungen
        </h1>
        <p className="text-[13px] text-navy-dark/70 leading-relaxed mt-2 max-w-2xl">
          Rechnungen von geloeschten Konten. Diese Belege bleiben aus
          steuerrechtlichen Gruenden 10 Jahre erhalten und sind nur fuer
          Finance-Admins sichtbar. Personenbezogene Daten der Kunden sind
          anonymisiert.
        </p>
      </div>

      {state.loading ? (
        <div className="text-[13px] text-navy-dark/60 label-caps" data-testid="finance-archive-loading">
          Wird geladen …
        </div>
      ) : state.rows.length === 0 ? (
        <div
          className="bg-white border p-10 text-center"
          style={{ borderColor: withAlpha(BRAND_COLORS.navy, 0.1) }}
          data-testid="finance-archive-empty"
        >
          <FileText className="w-8 h-8 mx-auto mb-3 text-navy-dark/40" strokeWidth={1.4} />
          <div className="label-caps mb-1">Keine archivierten Rechnungen</div>
          <p className="text-[12px] text-navy-dark/60 mt-2">
            Sobald ein Konto geloescht wird, erscheinen die zugehoerigen
            Rechnungen hier — ausschliesslich fuer Finance-Admins.
          </p>
        </div>
      ) : (
        <div className="bg-white border" style={{ borderColor: withAlpha(BRAND_COLORS.navy, 0.12) }}>
          <table className="w-full text-[13px]" data-testid="finance-archive-table">
            <thead>
              <tr className="text-left border-b" style={{ borderColor: withAlpha(BRAND_COLORS.navy, 0.1) }}>
                <th className="px-4 py-3 label-caps">Nr.</th>
                <th className="px-4 py-3 label-caps">Titel</th>
                <th className="px-4 py-3 label-caps">Kunde</th>
                <th className="px-4 py-3 label-caps">Ausgestellt</th>
                <th className="px-4 py-3 label-caps text-right">Summe</th>
                <th className="px-4 py-3 label-caps">Status</th>
              </tr>
            </thead>
            <tbody>
              {state.rows.map((r) => (
                <tr
                  key={r.id}
                  data-testid={`finance-archive-row-${r.id}`}
                  className="border-b hover:bg-navy/[0.02]"
                  style={{ borderColor: withAlpha(BRAND_COLORS.navy, 0.06) }}
                >
                  <td className="px-4 py-3 font-mono text-navy-dark">{r.number}</td>
                  <td className="px-4 py-3 text-navy-dark">{r.title}</td>
                  <td className="px-4 py-3 italic text-navy-dark/60">{r.customer_name}</td>
                  <td className="px-4 py-3 text-navy-dark/70">{fmtDate(r.issued_at)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-navy-dark">
                    {fmtEur(r.total)}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className="inline-flex items-center px-2 py-0.5 text-[10px] tracking-widest uppercase"
                      style={{
                        background: withAlpha(BRAND_COLORS.navy, 0.08),
                        color: BRAND_COLORS.navy,
                      }}
                    >
                      {r.status || "—"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
